import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Reveal, Label } from "./Reveal";
import { MOTORCYCLES, SCOOTERS, VIDA } from "../../data/site";
import { submitLead, submitBooking } from "../../lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { CalendarDays, Loader2, CheckCircle2, Zap } from "lucide-react";

const CATEGORIES = ["Motorcycle", "Scooter", "VIDA Electric", "Not sure yet"];
const ALL_VEHICLES = [...MOTORCYCLES, ...SCOOTERS, ...VIDA].map((v) => v.name);

const field =
  "w-full bg-black border border-white/15 focus:border-red-600 outline-none text-white placeholder:text-zinc-500 px-4 py-3 transition-colors";

export const BookingForms = ({ prefillVehicle, onConsumed }) => {
  const [lead, setLead] = useState({ name: "", mobile: "", category: "Motorcycle", vehicle: "", pincode: "", test_drive: false, message: "" });
  const [leadLoading, setLeadLoading] = useState(false);
  const [leadDone, setLeadDone] = useState(false);

  const [svc, setSvc] = useState({ name: "", mobile: "", vehicle_reg_no: "", booking_date: "", pincode: "" });
  const [svcLoading, setSvcLoading] = useState(false);
  const [svcDone, setSvcDone] = useState(false);

  useEffect(() => {
    if (prefillVehicle) {
      setLead((p) => ({ ...p, vehicle: prefillVehicle }));
      onConsumed?.();
    }
  }, [prefillVehicle, onConsumed]);

  const validMobile = (m) => m.replace(/\D/g, "").length >= 10;
  const validPin = (p) => /^\d{6}$/.test((p || "").trim());

  const handleLead = async (e) => {
    e.preventDefault();
    if (!lead.name.trim() || !validMobile(lead.mobile)) {
      toast.error("Please enter your name and a valid 10-digit mobile number.");
      return;
    }
    if (lead.test_drive && !validPin(lead.pincode)) {
      toast.error("For a test ride, please enter your 6-digit area pincode.");
      return;
    }
    setLeadLoading(true);
    try {
      await submitLead(lead);
      setLeadDone(true);
      toast.success(lead.test_drive ? "Test ride requested! We'll call to schedule it." : "Thanks! Our sales team will call you shortly.");
      setLead({ name: "", mobile: "", category: "Motorcycle", vehicle: "", pincode: "", test_drive: false, message: "" });
    } catch {
      toast.error("Something went wrong. Please call us at 92814 52805.");
    } finally {
      setLeadLoading(false);
    }
  };

  const handleSvc = async (e) => {
    e.preventDefault();
    if (!svc.name.trim() || !validMobile(svc.mobile) || !svc.vehicle_reg_no.trim() || !svc.booking_date) {
      toast.error("Please fill name, mobile, vehicle registration no. and a booking date.");
      return;
    }
    setSvcLoading(true);
    try {
      await submitBooking(svc);
      setSvcDone(true);
      toast.success("Service booked! Our team will confirm your slot tomorrow.");
      setSvc({ name: "", mobile: "", vehicle_reg_no: "", booking_date: "", pincode: "" });
    } catch {
      toast.error("Something went wrong. Please call us at 92814 52805.");
    } finally {
      setSvcLoading(false);
    }
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <section id="service" className="relative py-24 md:py-32 bg-[#0a0a0a]">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <Reveal>
          <Label>Get on the road</Label>
          <h2 className="mt-4 font-heading text-4xl sm:text-6xl font-bold uppercase tracking-tighter text-white">
            Enquire & <span className="text-red-600">book</span>
          </h2>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* ---------------- Lead / Buying interest + Test ride ---------------- */}
          <Reveal>
            <div id="enquire" className="border border-white/10 bg-[#111] p-6 md:p-9 h-full">
              <h3 className="font-heading text-2xl font-semibold uppercase text-white">Interested in buying?</h3>
              <p className="text-zinc-400 mt-2 text-sm">Tell us what you like — we'll help you pick, price, finance it or bring a test ride to you.</p>

              {leadDone ? (
                <div className="mt-10 flex flex-col items-center text-center py-10" data-testid="lead-success">
                  <CheckCircle2 className="text-red-600" size={48} />
                  <p className="mt-4 font-heading text-xl uppercase text-white">Enquiry received</p>
                  <p className="text-zinc-400 mt-1 text-sm">Our team will reach out to you soon.</p>
                  <button onClick={() => setLeadDone(false)} className="mt-6 text-sm font-bold uppercase text-red-500" data-testid="lead-new-btn">
                    Send another
                  </button>
                </div>
              ) : (
                <form onSubmit={handleLead} className="mt-6 space-y-4" data-testid="lead-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input className={field} placeholder="Your name" value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} data-testid="lead-name" />
                    <input className={field} placeholder="Mobile number" inputMode="numeric" value={lead.mobile} onChange={(e) => setLead({ ...lead, mobile: e.target.value })} data-testid="lead-mobile" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Select value={lead.category} onValueChange={(v) => setLead({ ...lead, category: v })}>
                      <SelectTrigger className={field + " h-auto"} data-testid="lead-category"><SelectValue placeholder="Category" /></SelectTrigger>
                      <SelectContent className="bg-[#161616] border-white/15 text-white">
                        {CATEGORIES.map((c) => <SelectItem key={c} value={c} className="focus:bg-red-600/20 focus:text-white">{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={lead.vehicle || undefined} onValueChange={(v) => setLead({ ...lead, vehicle: v })}>
                      <SelectTrigger className={field + " h-auto"} data-testid="lead-vehicle"><SelectValue placeholder="Preferred model (optional)" /></SelectTrigger>
                      <SelectContent className="bg-[#161616] border-white/15 text-white max-h-64">
                        {ALL_VEHICLES.map((v) => <SelectItem key={v} value={v} className="focus:bg-red-600/20 focus:text-white">{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Test ride toggle */}
                  <button
                    type="button"
                    onClick={() => setLead({ ...lead, test_drive: !lead.test_drive })}
                    data-testid="lead-testdrive-toggle"
                    className={`w-full flex items-center gap-3 border px-4 py-3 text-left transition-colors ${lead.test_drive ? "border-red-600 bg-red-600/10" : "border-white/15 hover:border-white/30"}`}
                  >
                    <span className={`flex items-center justify-center w-9 h-9 shrink-0 ${lead.test_drive ? "bg-red-600" : "bg-white/10"}`}>
                      <Zap size={16} className="text-white" />
                    </span>
                    <span>
                      <span className="block text-sm font-bold uppercase tracking-wide text-white">Book a free test ride</span>
                      <span className="block text-xs text-zinc-400">We'll bring the bike to your doorstep</span>
                    </span>
                    <span className={`ml-auto w-5 h-5 border ${lead.test_drive ? "bg-red-600 border-red-600" : "border-white/30"} flex items-center justify-center`}>
                      {lead.test_drive && <CheckCircle2 size={14} className="text-white" />}
                    </span>
                  </button>

                  <input
                    className={field}
                    placeholder={lead.test_drive ? "Area pincode (required for test ride)" : "Area pincode (optional)"}
                    inputMode="numeric"
                    maxLength={6}
                    value={lead.pincode}
                    onChange={(e) => setLead({ ...lead, pincode: e.target.value })}
                    data-testid="lead-pincode"
                  />
                  <textarea className={field + " resize-none"} rows={2} placeholder="Anything else? (exchange, finance, colour...)" value={lead.message} onChange={(e) => setLead({ ...lead, message: e.target.value })} data-testid="lead-message" />
                  <button type="submit" disabled={leadLoading} className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-bold uppercase tracking-wide py-4 transition-colors" data-testid="lead-submit">
                    {leadLoading ? <Loader2 className="animate-spin" size={18} /> : lead.test_drive ? "Request test ride" : "Request a call back"}
                  </button>
                </form>
              )}
            </div>
          </Reveal>

          {/* ---------------- Service booking ---------------- */}
          <Reveal delay={0.1}>
            <div className="border border-white/10 bg-[#111] p-6 md:p-9 h-full">
              <div className="flex items-center gap-3">
                <CalendarDays className="text-red-600" size={22} />
                <h3 className="font-heading text-2xl font-semibold uppercase text-white">Book a service</h3>
              </div>
              <p className="text-zinc-400 mt-2 text-sm">Drop your details — our service team follows up the very next day to confirm your slot.</p>

              {svcDone ? (
                <div className="mt-10 flex flex-col items-center text-center py-10" data-testid="service-success">
                  <CheckCircle2 className="text-red-600" size={48} />
                  <p className="mt-4 font-heading text-xl uppercase text-white">Booking received</p>
                  <p className="text-zinc-400 mt-1 text-sm">We'll call you tomorrow to confirm.</p>
                  <button onClick={() => setSvcDone(false)} className="mt-6 text-sm font-bold uppercase text-red-500" data-testid="service-new-btn">
                    Book another
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSvc} className="mt-6 space-y-4" data-testid="service-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input className={field} placeholder="Customer name" value={svc.name} onChange={(e) => setSvc({ ...svc, name: e.target.value })} data-testid="service-name" />
                    <input className={field} placeholder="Phone number" inputMode="numeric" value={svc.mobile} onChange={(e) => setSvc({ ...svc, mobile: e.target.value })} data-testid="service-mobile" />
                  </div>
                  <input className={field + " uppercase"} placeholder="Vehicle registration no. (e.g. AP21 AB 1234)" value={svc.vehicle_reg_no} onChange={(e) => setSvc({ ...svc, vehicle_reg_no: e.target.value.toUpperCase() })} data-testid="service-regno" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <span className="text-xs uppercase tracking-widest text-zinc-500">Service date</span>
                      <input type="date" min={today} className={field + " mt-1 [color-scheme:dark]"} value={svc.booking_date} onChange={(e) => setSvc({ ...svc, booking_date: e.target.value })} data-testid="service-date" />
                    </div>
                    <div>
                      <span className="text-xs uppercase tracking-widest text-zinc-500">Location pincode</span>
                      <input className={field + " mt-1"} placeholder="6-digit pincode" inputMode="numeric" maxLength={6} value={svc.pincode} onChange={(e) => setSvc({ ...svc, pincode: e.target.value })} data-testid="service-pincode" />
                    </div>
                  </div>
                  <button type="submit" disabled={svcLoading} className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-bold uppercase tracking-wide py-4 transition-colors" data-testid="service-submit">
                    {svcLoading ? <Loader2 className="animate-spin" size={18} /> : "Book service slot"}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
